import classNames from 'clsx';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { CasinoGameHeader, CasinoGameList, useWidgetAnchor } from '@swf/casino';
import { CacheControl, Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import type { SliderHandles, State as SliderState } from '@swf/components/Slider/Slider';
import { Header } from '@swf/core/enums';
import { type RootState, select } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import { getStyle, isVisibleByAuth } from '@swf/core/utils/casino';

import styles from './CasinoMosaicWidget.scss';
import { mosaicWidgetModel, useMosaicWidgetModel } from './model';

const CasinoMosaicWidget: WidgetFC = () => {
  const { widgetConfig, widgetDispatch, widgetSelect } = useMosaicWidgetModel();
  const data = useSelector(widgetSelect.get);
  const { completed, realGames = [] } = data;
  const containerRef = React.createRef<HTMLDivElement>();
  const sliderRef = React.createRef<SliderHandles>();
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const widgetTargetId = useWidgetAnchor();
  const {
    layout,
    hide_on_login: hideOnLogin,
    logged_in_only: loggedInOnly,
    display_title: displayTitle,
    sub_title: subTitle,
    more_link: moreLink,
    overlay_hierarchy: overlayHierarchy
  } = widgetConfig;
  const [sliderState, setSliderState] = useState<SliderState>({ left: false, right: false });

  const loading =
    data.stream1.loading || data?.stream2?.loading || data?.stream3?.loading || data.loading;

  const localConfig = useSelector((state: RootState) =>
    widgetSelect.getLocalConfig(state, widgetConfig)
  );

  useEffect(() => {
    if (!completed && !sliderState.right && sliderState.left) {
      widgetDispatch.loadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sliderState.left, sliderState.right, completed]);

  const minimumGames =
    localConfig?.columnConfigs.reduce(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (result: number, value: any) => value.gamesPer + result,
      0
    ) || 4;

  const shouldRender =
    realGames.length >= minimumGames &&
    isVisibleByAuth(loggedInOnly, hideOnLogin, isAuthenticated) &&
    (realGames.length || !loading);

  return shouldRender ? (
    <section
      className={classNames(styles.container)}
      ref={containerRef}
      id={widgetTargetId}
      style={getStyle(layout)}
    >
      <CacheControl>public, max-age=600</CacheControl>
      <Vary by={[Header.Currency]} />
      <CasinoGameHeader
        {...widgetConfig}
        title={displayTitle}
        subTitle={subTitle}
        moreLink={moreLink}
        sliderRef={sliderRef}
        sliderState={sliderState}
        icon="icnSquare"
      />
      <CasinoGameList
        preload
        games={realGames}
        icon="icnSquare"
        mosaic={localConfig.layout}
        sliderRef={sliderRef}
        onScroll={setSliderState}
        overlayHierarchy={overlayHierarchy}
      />
    </section>
  ) : null;
};

CasinoMosaicWidget.model = mosaicWidgetModel;

export default CasinoMosaicWidget;
